import Feature from "ol/Feature";
import PropTypes from "prop-types";
import qs from "query-string";
import { Component, createRef } from "react";
import { MdClose } from "react-icons/md";
import { connect } from "react-redux";

import { TRACK_EVENT_CATEGORY_DEPARTURES } from "../constants";
import {
  setSelectedVehicles,
  setStation,
  trackEvent,
  unsubscribeDepartures,
} from "../model/actions";
import AppPropTypes from "../model/propTypes";
import TralisLayer from "../TralisLayer";
import {
  hasRealtimeDeparture,
  //getNotificationsForStation,
  isDepartureCancelled,
} from "../utils";

import Button from "./controls/Button";
import DepartureDestination from "./departure/DepartureDestination";
import DepartureInfo from "./departure/DepartureInfo";
import DepartureTime from "./departure/DepartureTime";
import Platform from "./departure/Platform";
import TrainIcon from "./departure/TrainIcon";
import DepartureDebugInfo from "./DepartureDebugInfo";
import LineIconSvg from "./LineIconSvg";
import RealtimeLayer from "./RealtimeLayer";
import StationBanner from "./StationBanner";
import StationTitle from "./StationTitle";

// import getTestDeparture from '../utils/getTestDeparture'; // KEEP FOR TESTING
import "./Departures.scss";

const propTypes = {
  debug: AppPropTypes.debugMode,
  // notifications: PropTypes.arrayOf(PropTypes.object),
  departures: AppPropTypes.departures,
  departuresListCloseButton: PropTypes.bool,
  departuresLoading: PropTypes.bool,
  dispatchSetSelectedVehicles: PropTypes.func.isRequired,
  dispatchSetStation: PropTypes.func.isRequired,
  dispatchTrackEvent: PropTypes.func.isRequired,
  dispatchUnsubscribeDepartures: PropTypes.func.isRequired,
  incidentProgram: PropTypes.bool,
  initialStationLoading: PropTypes.bool,

  notifications: PropTypes.arrayOf(PropTypes.object),
  realtimeLayer: PropTypes.instanceOf(TralisLayer),
  station: PropTypes.instanceOf(Feature),
};

const defaultProps = {
  debug: false,
  departures: [],
  departuresListCloseButton: true,
  departuresLoading: false,
  incidentProgram: false,
  initialStationLoading: false,
  // notifications: [],
  realtimeLayer: null,
  station: null,
};

class Departures extends Component {
  /**
   * Contructor.
   * @param {Object} props Component properties.
   */
  constructor(props) {
    super(props);

    this.state = {
      pulseClass: "",
    };

    const queryObj = qs.parse(window.location.search) || {};
    this.simulateIncident = queryObj.incident === "true";
    this.departureRef = createRef();
  }

  /**
   * Remote all departures and disconnect.
   */
  clearDepartures() {
    const { dispatchSetStation, dispatchUnsubscribeDepartures } = this.props;
    dispatchUnsubscribeDepartures();
    dispatchSetStation(null);
  }

  componentDidUpdate(prevProps) {
    const { departures, departuresLoading, initialStationLoading, station } =
      this.props;

    if (
      station &&
      departures.length &&
      !initialStationLoading &&
      !departuresLoading &&
      prevProps.departuresLoading &&
      this.departureRef?.current
    ) {
      // Scroll the departure into view if clicked.
      this.departureRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }

  /**
   * Return false if the departure should be displayed.
   * If train does not have fzo or if it's cancelled,
   * the train is hidden using RIS time.
   */
  hideDeparture(departure) {
    const { incidentProgram } = this.props;
    const { has_fzo: hasFzo, state, time } = departure;

    if (
      (this.simulateIncident || incidentProgram) &&
      isDepartureCancelled(departure)
    ) {
      return true;
    }

    if (state === "HIDDEN" || state === "LEAVING") {
      return true;
    }
    const timeDiff = new Date(time) - new Date();

    if (timeDiff < 0 && (!hasFzo || !state || state === "JOURNEY_CANCELLED")) {
      return true;
    }
    return false;
  }

  /**
   * Called if a departure is clicked.
   */
  async onDepartureClick(departure) {
    const { dispatchSetSelectedVehicles, dispatchTrackEvent, realtimeLayer } =
      this.props;

    // geofox departures haven't train_id
    if (isDepartureCancelled(departure) || !departure.train_id) {
      return;
    }
    let vehicle = realtimeLayer?.trajectories[departure.train_id]?.properties;

    if (!vehicle) {
      // Get the vehicle from tralisAPI
      const message = await (realtimeLayer?.api?.getTrajectory(
        departure.train_id,
      ) || Promise.resolve({}));

      if (message.content) {
        vehicle = {
          ...(message.content?.properties || {}),
          departure, // this object is used to display the Ris Popup when no realtime
        };
        realtimeLayer.addTrajectory(message.content);
      }
    }

    // The vehicle is not yet drawable on the map but we still want to display the stopSequence and the fullTrajectory
    if (
      !vehicle &&
      departure.train_id &&
      departure.train_number &&
      departure.line
    ) {
      vehicle = {
        line: departure.line,
        train_id: departure.train_id,
        train_number: departure.train_number,
      };
    }

    if (vehicle) {
      dispatchSetSelectedVehicles([vehicle]);
      const action = departure.has_fzo ? "selectVehicleFZO" : "selectVehicle";

      dispatchTrackEvent(
        TRACK_EVENT_CATEGORY_DEPARTURES,
        action,
        departure.line?.name,
      );
    }
  }

  render() {
    const { pulseClass } = this.state;
    const {
      debug,
      departures,
      departuresListCloseButton,
      departuresLoading,
      incidentProgram,
      // notifications,
      station,
    } = this.props;

    if (station && !departuresLoading && !departures.length) {
      return (
        <div>
          <div id="info-text">
            Keine Abfahrten verfügbar.
            {departuresListCloseButton && (
              <Button
                className="close-button"
                onClick={() => this.clearDepartures()}
                tabIndex={-2}
              >
                <MdClose focusable={false} />
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (!station || !departures.length) {
      return null;
    }

    const classes = [`${departuresLoading ? "out" : "in"}`, pulseClass];
    return (
      <div
        className={classes.join(" ")}
        id="departures"
        ref={this.departureRef}
      >
        <StationTitle />
        <StationBanner />
        <div className="head departures">
          <div className="col line">Linie</div>
          <div className="col destination">Ziel</div>
          <div className="col platform">Gleis</div>
          <div className="col part" />
          <div className="col time">in Min</div>
        </div>

        <div className="body list">
          {departures
            .filter((departure) => !this.hideDeparture(departure))
            .map((departure) => {
              // departure = getTestDeparture(); // KEEP FOR TESTING
              const isCancelled = isDepartureCancelled(departure);
              const noRealtime = !hasRealtimeDeparture(departure);
              const noRouteInfo =
                isCancelled ||
                !departure.train_id ||
                !departure.train_number ||
                !departure.line;

              const classNames = ["item"];

              if (debug && departure.selected) {
                classNames.push("selected");
              }

              if (departure.has_fzo) {
                classNames.push("fzo");
              } else {
                classNames.push("ris");
              }

              if (isCancelled) {
                classNames.push("cancelled");
              }

              if (noRouteInfo) {
                classNames.push("no-route-info");
              }

              return (
                <button
                  className={classNames.join(" ")}
                  key={departure.call_id}
                  onClick={() => this.onDepartureClick(departure)}
                  type="button"
                >
                  <div className="item-inner">
                    <div
                      style={{
                        alignItems: "center",
                        display: "flex",
                        overflow: "hidden",
                      }}
                    >
                      <div className="line col">
                        <LineIconSvg line={departure.line} />
                      </div>

                      <DepartureDestination
                        departure={departure}
                        incidentProgram={
                          this.simulateIncident || incidentProgram
                        }
                      />

                      <div className="platform col">
                        <Platform departure={departure} />
                      </div>
                      <div className="part col">
                        <TrainIcon departure={departure} />
                      </div>
                      <div className="time col">
                        <DepartureTime
                          debug={debug}
                          departure={departure}
                          lineColor
                        />
                      </div>
                    </div>
                    <DepartureDebugInfo departure={departure} />
                    <DepartureInfo
                      departure={departure}
                      incidentProgram={this.simulateIncident || incidentProgram}
                    />
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    );
  }
}

Departures.propTypes = propTypes;
Departures.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  debug: state.debug,
  departures: state.departures,
  departuresListCloseButton: state.config?.departuresListCloseButton,
  departuresLoading: state.departuresLoading,
  incidentProgram: state.incidentProgram,
  initialStationLoading: state.initialStationLoading,
  notifications: state.notifications,
  realtimeLayer: state.realtimeLayer,
  station: state.station,
});

const mapDispatchToProps = {
  dispatchSetSelectedVehicles: setSelectedVehicles,
  dispatchSetStation: setStation,
  dispatchTrackEvent: trackEvent,
  dispatchUnsubscribeDepartures: unsubscribeDepartures,
};

export default connect(mapStateToProps, mapDispatchToProps)(Departures);
